import type { SubscriptionTier } from "@investhinks/api-client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";

import { subscriptionTiersEndpoints } from "../../../../api";

// Local types for this component
interface TierUser {
  id: number | string;
  name?: string;
  username?: string;
  email?: string;
}

interface TierOption {
  id: string;
  name: string;
  userCount: number;
}

interface DragState {
  user: TierUser | null;
  source: string | null;
}

interface TierMigrationModalProps {
  isOpen: boolean;
  toggle: () => void;
  tiers: SubscriptionTier[];
  usersWithoutTier: number;
  onMigrationComplete?: (count: number) => void;
}

const TierMigrationModal: React.FC<TierMigrationModalProps> = ({
  isOpen,
  toggle,
  tiers,
  usersWithoutTier,
  onMigrationComplete,
}) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "settings.migration" });

  // Selected tiers
  const [leftTierId, setLeftTierId] = useState<string>("");
  const [rightTierId, setRightTierId] = useState<string>("");

  // Users lists
  const [leftUsers, setLeftUsers] = useState<TierUser[]>([]);
  const [rightUsers, setRightUsers] = useState<TierUser[]>([]);

  // Loading states
  const [leftLoading, setLeftLoading] = useState<boolean>(false);
  const [rightLoading, setRightLoading] = useState<boolean>(false);

  // Search states
  const [leftSearch, setLeftSearch] = useState<string>("");
  const [rightSearch, setRightSearch] = useState<string>("");

  // Selected users (for transfer) - use object instead of Set for stable reference
  const [leftSelected, setLeftSelected] = useState<Record<string | number, boolean>>({});
  const [rightSelected, setRightSelected] = useState<Record<string | number, boolean>>({});

  // Migration state
  const [migrating, setMigrating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Pending changes
  const [pendingMoveRight, setPendingMoveRight] = useState<TierUser[]>([]);
  const [pendingMoveLeft, setPendingMoveLeft] = useState<TierUser[]>([]);

  // Drag ref to avoid state updates during drag
  const dragRef = useRef<DragState>({ user: null, source: null });

  // Get tier options
  const tierOptions: TierOption[] = [
    { id: "0", name: t("noTier"), userCount: usersWithoutTier },
    ...tiers.map((tier) => ({
      id: String(tier.id),
      name: tier.name,
      userCount: tier.userCount || 0,
    })),
  ];

  // Load users
  const loadUsers = useCallback(
    async (
      tierId: string,
      setUsers: React.Dispatch<React.SetStateAction<TierUser[]>>,
      setLoading: React.Dispatch<React.SetStateAction<boolean>>,
      search = ""
    ) => {
      if (!tierId) {
        setUsers([]);
        return;
      }
      setLoading(true);
      try {
        // Convert tierId to number
        const parsedTierId = parseInt(tierId, 10);
        const { data: users } = await subscriptionTiersEndpoints.getUsersByTier(parsedTierId, search);
        setUsers(users || []);
      } catch (err) {
        console.error("Error loading users:", err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Load users when tier changes (no search param - filter locally)
  useEffect(() => {
    loadUsers(leftTierId, setLeftUsers, setLeftLoading);
  }, [leftTierId, loadUsers]);

  useEffect(() => {
    loadUsers(rightTierId, setRightUsers, setRightLoading);
  }, [rightTierId, loadUsers]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setLeftTierId("");
      setRightTierId("");
      setLeftUsers([]);
      setRightUsers([]);
      setLeftSearch("");
      setRightSearch("");
      setLeftSelected({});
      setRightSelected({});
      setPendingMoveRight([]);
      setPendingMoveLeft([]);
      setError(null);
    }
  }, [isOpen]);

  // Selection handlers
  const toggleLeftSelection = useCallback((userId: number | string) => {
    setLeftSelected((prev) => {
      const next = { ...prev };
      if (next[userId]) {
        delete next[userId];
      } else {
        next[userId] = true;
      }
      return next;
    });
  }, []);

  const toggleRightSelection = useCallback((userId: number | string) => {
    setRightSelected((prev) => {
      const next = { ...prev };
      if (next[userId]) {
        delete next[userId];
      } else {
        next[userId] = true;
      }
      return next;
    });
  }, []);

  // Filter users by search term (local filtering)
  const filterBySearch = useCallback((users: TierUser[], search: string): TierUser[] => {
    if (!search.trim()) return users;
    const term = search.toLowerCase().trim();
    return users.filter(
      (u) =>
        (u.name && u.name.toLowerCase().includes(term)) ||
        (u.username && u.username.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term))
    );
  }, []);

  // Get visible users (filtered by search + pending changes)
  const getVisibleLeftUsers = useCallback(() => {
    const pendingRightIds = new Set(pendingMoveRight.map((u) => u.id));
    const baseUsers = [...leftUsers.filter((u) => !pendingRightIds.has(u.id)), ...pendingMoveLeft];
    return filterBySearch(baseUsers, leftSearch);
  }, [leftUsers, pendingMoveRight, pendingMoveLeft, leftSearch, filterBySearch]);

  const getVisibleRightUsers = useCallback(() => {
    const pendingLeftIds = new Set(pendingMoveLeft.map((u) => u.id));
    const baseUsers = [...rightUsers.filter((u) => !pendingLeftIds.has(u.id)), ...pendingMoveRight];
    return filterBySearch(baseUsers, rightSearch);
  }, [rightUsers, pendingMoveLeft, pendingMoveRight, rightSearch, filterBySearch]);

  // Move handlers
  const moveSelectedRight = useCallback(() => {
    const selectedIds = Object.keys(leftSelected);
    if (!rightTierId || selectedIds.length === 0) return;

    const usersToMove = leftUsers.filter((u) => leftSelected[u.id]);
    const alreadyPendingLeft = pendingMoveLeft.filter((u) => leftSelected[u.id]);

    setPendingMoveLeft((prev) => prev.filter((u) => !leftSelected[u.id]));
    const newPending = usersToMove.filter((u) => !alreadyPendingLeft.some((p) => p.id === u.id));
    setPendingMoveRight((prev) => [...prev, ...newPending]);
    setLeftSelected({});
  }, [leftSelected, rightTierId, leftUsers, pendingMoveLeft]);

  const moveSelectedLeft = useCallback(() => {
    const selectedIds = Object.keys(rightSelected);
    if (!leftTierId || selectedIds.length === 0) return;

    const usersToMove = rightUsers.filter((u) => rightSelected[u.id]);
    const alreadyPendingRight = pendingMoveRight.filter((u) => rightSelected[u.id]);

    setPendingMoveRight((prev) => prev.filter((u) => !rightSelected[u.id]));
    const newPending = usersToMove.filter((u) => !alreadyPendingRight.some((p) => p.id === u.id));
    setPendingMoveLeft((prev) => [...prev, ...newPending]);
    setRightSelected({});
  }, [rightSelected, leftTierId, rightUsers, pendingMoveRight]);

  // Move single user (for drag and drop)
  const moveSingleUser = useCallback(
    (user: TierUser, toRight: boolean) => {
      if (toRight && rightTierId) {
        if (pendingMoveLeft.some((u) => u.id === user.id)) {
          setPendingMoveLeft((prev) => prev.filter((u) => u.id !== user.id));
        } else if (!pendingMoveRight.some((u) => u.id === user.id)) {
          setPendingMoveRight((prev) => [...prev, user]);
        }
      } else if (!toRight && leftTierId) {
        if (pendingMoveRight.some((u) => u.id === user.id)) {
          setPendingMoveRight((prev) => prev.filter((u) => u.id !== user.id));
        } else if (!pendingMoveLeft.some((u) => u.id === user.id)) {
          setPendingMoveLeft((prev) => [...prev, user]);
        }
      }
    },
    [rightTierId, leftTierId, pendingMoveLeft, pendingMoveRight]
  );

  // Pending check
  const isPendingMoveRight = useCallback(
    (userId: number | string) => pendingMoveRight.some((u) => u.id === userId),
    [pendingMoveRight]
  );
  const isPendingMoveLeft = useCallback(
    (userId: number | string) => pendingMoveLeft.some((u) => u.id === userId),
    [pendingMoveLeft]
  );

  // Save
  const handleSave = async () => {
    if (pendingMoveRight.length === 0 && pendingMoveLeft.length === 0) {
      toggle();
      return;
    }

    setMigrating(true);
    setError(null);

    try {
      if (pendingMoveRight.length > 0) {
        const toTierId = parseInt(rightTierId, 10);
        await subscriptionTiersEndpoints.migrateUsers({
          toTierId,
          userIds: pendingMoveRight.map((u) => Number(u.id)),
        });
      }
      if (pendingMoveLeft.length > 0) {
        const toTierId = parseInt(leftTierId, 10);
        await subscriptionTiersEndpoints.migrateUsers({
          toTierId,
          userIds: pendingMoveLeft.map((u) => Number(u.id)),
        });
      }
      if (onMigrationComplete) {
        onMigrationComplete(pendingMoveRight.length + pendingMoveLeft.length);
      }
      toggle();
    } catch (err: unknown) {
      console.error("Migration error:", err);
      const errorMessage = err instanceof Error ? err.message : t("migrationError");
      setError(errorMessage);
    } finally {
      setMigrating(false);
    }
  };

  const leftSelectedCount = Object.keys(leftSelected).length;
  const rightSelectedCount = Object.keys(rightSelected).length;
  const totalPendingChanges = pendingMoveRight.length + pendingMoveLeft.length;

  // Drag handlers using ref to avoid re-renders
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, user: TierUser, source: string) => {
    dragRef.current = { user, source };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(user.id));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetSide: string) => {
    e.preventDefault();
    const { user, source } = dragRef.current;

    if (!user || source === targetSide) {
      dragRef.current = { user: null, source: null };
      return;
    }

    if (targetSide === "right" && rightTierId) {
      moveSingleUser(user, true);
    } else if (targetSide === "left" && leftTierId) {
      moveSingleUser(user, false);
    }

    dragRef.current = { user: null, source: null };
  };

  // Render user item
  const renderUserItem = (user: TierUser, side: "left" | "right") => {
    const isLeft = side === "left";
    const isSelected = isLeft ? leftSelected[user.id] : rightSelected[user.id];
    const isPending = isLeft ? isPendingMoveRight(user.id) : isPendingMoveLeft(user.id);
    const pendingDirection = isLeft ? "right" : "left";
    const onToggle = isLeft ? toggleLeftSelection : toggleRightSelection;
    const name = user.name || user.username || t("noName");
    const bgColor = isPending
      ? pendingDirection === "right"
        ? "#d4edda"
        : "#cce5ff"
      : isSelected
        ? "#e8f4fd"
        : "#fff";

    const borderLeftColor = isPending
      ? pendingDirection === "right"
        ? "#28a745"
        : "#007bff"
      : isSelected
        ? "#5e72e4"
        : "transparent";

    return (
      <div
        key={user.id}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 12px",
          cursor: "grab",
          backgroundColor: bgColor,
          borderLeft: `4px solid ${borderLeftColor}`,
          borderBottom: "1px solid #e9ecef",
          userSelect: "none",
        }}
        draggable="true"
        onDragStart={(e) => handleDragStart(e, user, side)}
      >
        <input
          type="checkbox"
          checked={!!isSelected}
          onChange={() => onToggle(user.id)}
          style={{ marginRight: "12px", cursor: "pointer" }}
        />
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: "0.875rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {name}
          </div>
          <div
            style={{
              color: "#8898aa",
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.email}
          </div>
        </div>
        {isPending && (
          <Badge
            color={pendingDirection === "right" ? "success" : "primary"}
            style={{ marginLeft: "8px", flexShrink: 0 }}
          >
            {pendingDirection === "right" ? "→" : "←"}
          </Badge>
        )}
      </div>
    );
  };

  // Render panel
  const renderPanel = (side: "left" | "right") => {
    const isLeft = side === "left";
    const tierId = isLeft ? leftTierId : rightTierId;
    const setTierId = isLeft ? setLeftTierId : setRightTierId;
    const users = isLeft ? getVisibleLeftUsers() : getVisibleRightUsers();
    const loading = isLeft ? leftLoading : rightLoading;
    const search = isLeft ? leftSearch : rightSearch;
    const setSearch = isLeft ? setLeftSearch : setRightSearch;
    const selected = isLeft ? leftSelected : rightSelected;
    const setSelected = isLeft ? setLeftSelected : setRightSelected;
    const otherTierId = isLeft ? rightTierId : leftTierId;

    const selectedCount = Object.keys(selected).length;

    const selectAll = () => {
      const allSelected: Record<string | number, boolean> = {};
      users.forEach((u) => {
        allSelected[u.id] = true;
      });
      setSelected(allSelected);
    };

    const clearSelection = () => setSelected({});

    return (
      <div
        style={{
          border: "1px solid #dee2e6",
          borderRadius: "0.375rem",
          backgroundColor: "#fff",
          height: "420px",
          display: "flex",
          flexDirection: "column",
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, side)}
      >
        {/* Tier selector */}
        <div style={{ padding: "12px", borderBottom: "1px solid #dee2e6", backgroundColor: "#f8f9fa" }}>
          <Input
            type="select"
            value={tierId}
            onChange={(e) => setTierId(e.target.value)}
            style={{ paddingRight: "2rem", backgroundColor: "#fff" }}
          >
            <option value="">{t("selectTier")}</option>
            {tierOptions
              .filter((opt) => opt.id !== otherTierId)
              .map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name} ({opt.userCount} {t("users")})
                </option>
              ))}
          </Input>
        </div>

        {/* Search + Selection bar combined */}
        <div
          style={{
            padding: "8px 12px",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div style={{ flex: 1, position: "relative" }}>
            <i
              className="fas fa-search"
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#8898aa",
                fontSize: "0.875rem",
                pointerEvents: "none",
              }}
            ></i>
            <Input
              type="search"
              name="tier-filter"
              id={`tier-filter-${side}`}
              placeholder={t("searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!tierId}
              style={{ paddingLeft: "32px" }}
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
              data-bwignore="1"
            />
          </div>
          {tierId && users.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              <Button color="link" size="sm" className="p-0" onClick={selectAll}>
                {t("selectAll")}
              </Button>
              {selectedCount > 0 && (
                <Button color="link" size="sm" className="p-0 text-muted" onClick={clearSelection}>
                  {t("clearSelection")}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Users list */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {!tierId ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#8898aa" }}>
              <i className="fas fa-layer-group fa-2x" style={{ display: "block", marginBottom: "8px" }}></i>
              <span>{t("selectTierFirst")}</span>
            </div>
          ) : loading ? (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <Spinner size="sm" color="primary" />
              <p style={{ marginTop: "8px", color: "#8898aa", marginBottom: 0 }}>{t("loadingUsers")}</p>
            </div>
          ) : users.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#8898aa" }}>
              <i className="fas fa-users fa-2x" style={{ display: "block", marginBottom: "8px" }}></i>
              <span>{search ? t("noUsersMatch") : t("noUsers")}</span>
            </div>
          ) : (
            users.map((user) => renderUserItem(user, side))
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
      <ModalHeader toggle={toggle}>
        <i className="fas fa-exchange-alt mr-2"></i>
        {t("title")}
      </ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {error}
          </Alert>
        )}

        {/* Pending changes indicator */}
        {totalPendingChanges > 0 && (
          <div
            style={{
              marginBottom: "12px",
              padding: "8px",
              backgroundColor: "#fff3cd",
              borderRadius: "4px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Badge color="warning" className="mr-2">
              {totalPendingChanges}
            </Badge>
            <span>
              pending changes
              {pendingMoveRight.length > 0 && (
                <span style={{ color: "#28a745", marginLeft: "8px" }}>({pendingMoveRight.length} right)</span>
              )}
              {pendingMoveLeft.length > 0 && (
                <span style={{ color: "#007bff", marginLeft: "8px" }}>({pendingMoveLeft.length} left)</span>
              )}
            </span>
          </div>
        )}

        <Row>
          {/* Left Column */}
          <Col md={6} style={{ paddingRight: "8px" }}>
            {renderPanel("left")}
            <Button
              color="success"
              block
              className="mt-2"
              onClick={moveSelectedRight}
              disabled={!rightTierId || leftSelectedCount === 0}
            >
              {t("moveRight")}
              {leftSelectedCount > 0 && ` (${leftSelectedCount})`}
              <i className="fas fa-arrow-right ml-2"></i>
            </Button>
          </Col>

          {/* Right Column */}
          <Col md={6} style={{ paddingLeft: "8px" }}>
            {renderPanel("right")}
            <Button
              color="primary"
              block
              className="mt-2"
              onClick={moveSelectedLeft}
              disabled={!leftTierId || rightSelectedCount === 0}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              {t("moveLeft")}
              {rightSelectedCount > 0 && ` (${rightSelectedCount})`}
            </Button>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <small className="text-muted mr-auto">
          <i className="fas fa-info-circle mr-1"></i>
          Drag users or select and use buttons above
        </small>
        <Button color="secondary" onClick={toggle} disabled={migrating}>
          {t("cancel")}
        </Button>
        <Button color="primary" onClick={handleSave} disabled={migrating || totalPendingChanges === 0}>
          {migrating ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("saving")}
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              {t("saveChanges")}
              {totalPendingChanges > 0 && ` (${totalPendingChanges})`}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TierMigrationModal;
